"use client";

import Image from "next/image";
import { type JSX } from "react";
import { useLocale, useTranslations } from "next-intl";
import { useSitecore } from "@sitecore-content-sdk/nextjs";

import { useEventContext } from "contexts/EventContext";

import Button from "../../../atoms/Button";
import Icon from "../../../atoms/Icon";
import RichTextWithConsent from "../../../atoms/RichTextWithConsent";
import VideoIframe from "../../../atoms/VideoIframe";
import RichTextWithTable from "../../../molecules/RichTextWithTable/RichTextWithTables";
import { formatEventDate } from "../../../utils/dateUtils";
import {
  getImageURLForCHPages,
  isExternalURL,
} from "../../../utils/contentHubComponentsUtils";
import { ICONS } from "../../../variables/icons";
import { capitalizeLanguageRegion } from "lib/utils";

export const Default = (): JSX.Element => {
  const { page } = useSitecore();
  const locale = useLocale();
  const localeCode = capitalizeLanguageRegion(locale);
  const t = useTranslations();
  const aboutThePresenterText = t.has("about-the-presenter-text")
    ? t("about-the-presenter-text")
    : "About the Presenter";

  const { event } = useEventContext();

  console.log("---->[EventDetails] event", event);

  if (page.mode.isEditing) {
    return (
      <div
        className="event-details ch-details max-w-6xl mx-auto bg-white"
        data-role="event-details-component"
      >
        <div className="flex flex-col lg:flex-row gap-10 mt-20">
          <div className="flex-1 space-y-4">
            <h1 className="text-[35px] tracking-[0.8px] lg:text-5xl font-bold text-ecolab-blue leading-[52.5px]">
              Event Title
            </h1>
            <div className="space-y-1">
              <div className="text-xl font-semibold text-gray-900">
                Event Date | Event Time
              </div>
              <div className="text-gray-600 font-medium">Event Time Zone</div>
            </div>
            <div className="space-y-1">
              <div className="text-blue-600 font-semibold">
                <span className="uppercase tracking-[0.8px] text-[16px] font-bold text-ecolab-blue">
                  Location:
                </span>{" "}
                <span className="tracking-[0.8px] text-[16px] font-bold text-ecolab-blue">
                  Event Location
                </span>
              </div>
            </div>
            <div className="text-gray text-[16px] tracking-[0.8px] leading-6">
              <p>Event Description</p>
            </div>
            <div className="pt-4">
              <button
                type="button"
                className="bg-ecolab-blue hover:bg-dark-blue text-white font-bold pl-8 pr-6 py-3.5 leading-5 rounded-full shadow-md transition-colors duration-200 cursor-pointer text-left"
              >
                Event CTA
              </button>
            </div>
          </div>
          <div className="shrink-0 lg:w-96">
            <div className="w-full h-64 lg:h-80 bg-linear-to-br from-blue-900 via-blue-700 to-cyan-400 rounded-lg overflow-hidden">
              <div className="w-full h-full bg-cover bg-center opacity-80">
                <div className="w-full h-full flex items-center justify-center">
                  <Image
                    src={"https://dummyimage.com/424x239/006ad3/fff.png"}
                    alt={"Event Image"}
                    width={424}
                    height={239}
                    className="object-cover w-full h-full"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!event) {
    return <></>;
  }

  const startDate = event?.StartDateTime ? new Date(event.StartDateTime) : null;
  const endDate = event?.EndDateTime ? new Date(event.EndDateTime) : null;
  const ON_DEMAND_EVENT_TYPE_ID = "Ecolab.EventTypes.EventOnDemandWebinarPage";
  const isOnDemand = event.EventType === ON_DEMAND_EVENT_TYPE_ID;
  const dateRange = formatEventDate(startDate, endDate, {
    includeTime: !isOnDemand,
  });

  const eventImageUrl = getImageURLForCHPages(
    event?.EventImage?.results?.[0]?.assetToPublicLink?.results,
  );

  const getEventDetails = () => {
    const eventDetailImageUrl = getImageURLForCHPages(
      event?.EventDetailedToAsset?.results?.[0]?.assetToPublicLink?.results,
    );

    if (
      event?.EventDetailedTitle ||
      event?.EventDetailsFeatureVideoUrl ||
      eventDetailImageUrl ||
      event?.EventDetailedDescription ||
      event?.EventDetailedPrimaryButtonCTAUrl ||
      event?.EventDeatiledSecondaryButtonCtaUrl
    ) {
      return (
        <div className="max-w-(--event-detail-container-width) mx-auto max-lg:px-4 empty:hidden">
          <div className="rounded-lg bg-white overflow-hidden shadow-[0_10px_20px_0_rgba(0,0,0,0.1)] mb-6">
            {(event?.EventDetailsFeatureVideoUrl || eventDetailImageUrl) && (
              <div className="shrink-0 w-full h-auto bg-cover bg-center ">
                {event?.EventDetailsFeatureVideoUrl ? (
                  <div className="w-full mb-6">
                    <VideoIframe src={event.EventDetailsFeatureVideoUrl} />
                  </div>
                ) : (
                  <div className="w-full mb-6">
                    <Image
                      src={eventDetailImageUrl}
                      alt={"Event detail image"}
                      width={800}
                      height={450}
                      className="object-cover w-full h-full"
                    />
                  </div>
                )}
              </div>
            )}
            <div className="px-6 mb-6 empty:hidden first:mt-6">
              {event?.EventDetailedTitle && (
                <h3 className="text-ecolab-blue font-normal leading-9 text-[24px] mt-0 mb-6 ">
                  {event?.EventDetailedTitle}
                </h3>
              )}
              {event?.EventDetailedDescription && (
                <div className="font-normal text-charcoal-black space-y-1 wrap-anywhere mb-5 richtext-content">
                  <RichTextWithTable
                    field={{ value: event?.EventDetailedDescription }}
                  />
                </div>
              )}
              <div className="flex flex-wrap flex-col max-lg:items-start lg:flex-row max-lg:[&>a:last-child]:mb-0 empty:hidden md:gap-y-1">
                {event?.EventDetailedPrimaryButtonCTAUrl && (
                  <Button
                    buttonType="primary-with-icon"
                    field={{
                      value: {
                        href: event.EventDetailedPrimaryButtonCTAUrl,
                        text:
                          event?.EventDetailedPrimaryButtonCTAText ||
                          event.EventDetailedPrimaryButtonCTAUrl,
                        target: isExternalURL(
                          event.EventDetailedPrimaryButtonCTAUrl,
                        )
                          ? "_blank"
                          : "",
                      },
                    }}
                    tabIndex={0}
                    aria-label={event?.EventDetailedPrimaryButtonCTAText}
                    iconName="CHEVRON_BLUE_RIGHT"
                    data-role="link"
                  />
                )}
                {event?.EventDeatiledSecondaryButtonCtaUrl && (
                  <Button
                    buttonType="secondary-with-icon"
                    field={{
                      value: {
                        href: event?.EventDeatiledSecondaryButtonCtaUrl,
                        text:
                          event?.EventDetailedSecondaryButtonCTAText ||
                          event?.EventDeatiledSecondaryButtonCtaUrl,
                        target: isExternalURL(
                          event.EventDeatiledSecondaryButtonCtaUrl,
                        )
                          ? "_blank"
                          : "",
                      },
                    }}
                    tabIndex={0}
                    aria-label={event?.EventDetailedSecondaryButtonCTAText}
                    iconName="CHEVRON_BLUE_RIGHT"
                    data-role="link"
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <></>;
  };

  const getLocationDetails = () => {
    const eventLocationImageUrl = getImageURLForCHPages(
      event?.EventLocationImage?.results?.[0]?.assetToPublicLink?.results,
    );

    if (
      event?.EventLocationTitle ||
      event?.EventLocationFeatureVideoUrl ||
      eventLocationImageUrl ||
      event?.EventLocationDetails ||
      event?.LocationPrimaryButtonCtaUrl ||
      event?.LocationSecondaryButtonCtaUrl
    ) {
      return (
        <div className="max-w-(--event-detail-container-width) mx-auto max-lg:px-4 empty:hidden">
          <div className="mx-auto bg-white rounded-lg overflow-hidden shadow-[0_10px_20px_0_rgba(0,0,0,0.1)]">
            {(event?.EventLocationFeatureVideoUrl || eventLocationImageUrl) && (
              <div className="shrink-0 w-full h-auto bg-cover bg-center ">
                {event?.EventLocationFeatureVideoUrl ? (
                  <div className="w-full mb-6">
                    <VideoIframe src={event.EventLocationFeatureVideoUrl} />
                  </div>
                ) : (
                  <div className="w-full mb-6">
                    <Image
                      src={eventLocationImageUrl}
                      alt={"Event location image"}
                      width={800}
                      height={450}
                      className="object-cover w-full h-full"
                    />
                  </div>
                )}
              </div>
            )}
            <div className="px-6 mb-6 empty:hidden first:mt-6">
              {event?.EventLocationTitle && (
                <h3 className="text-ecolab-blue font-normal leading-9 text-[24px] mt-0 mb-6 lg:mb-5 ">
                  {event?.EventLocationTitle}
                </h3>
              )}
              {event?.EventLocationDetails && (
                <div className="font-normal text-charcoal-black space-y-1 wrap-anywhere mb-5 richtext-content">
                  <RichTextWithTable
                    field={{ value: event?.EventLocationDetails }}
                  />
                </div>
              )}
              {(event?.LocationPrimaryButtonCtaUrl ||
                event?.LocationSecondaryButtonCtaUrl) && (
                <div className="flex flex-wrap flex-col max-lg:items-start lg:flex-row max-lg:[&>a:last-child]:mb-0 empty:hidden md:gap-y-1">
                  {(event?.LocationPrimaryButtonCtaUrl ||
                    event?.LocationPrimaryButtonCTAText) && (
                    <Button
                      buttonType="primary-with-icon"
                      field={{
                        value: {
                          href: event.LocationPrimaryButtonCtaUrl,
                          text:
                            event?.LocationPrimaryButtonCTAText ||
                            event.LocationPrimaryButtonCtaUrl,
                          target: isExternalURL(
                            event.LocationPrimaryButtonCtaUrl,
                          )
                            ? "_blank"
                            : "",
                        },
                      }}
                      tabIndex={0}
                      aria-label={event?.LocationPrimaryButtonCTAText}
                      iconName="CHEVRON_BLUE_RIGHT"
                      data-role="link"
                    />
                  )}
                  {(event?.LocationSecondaryButtonCtaUrl ||
                    event?.LocationSecondaryButtonCtaText) && (
                    <Button
                      buttonType="secondary-with-icon"
                      field={{
                        value: {
                          href: event.LocationSecondaryButtonCtaUrl,
                          text:
                            event?.LocationSecondaryButtonCtaText ||
                            event.LocationSecondaryButtonCtaUrl,
                          target: isExternalURL(
                            event.LocationSecondaryButtonCtaUrl,
                          )
                            ? "_blank"
                            : "",
                        },
                      }}
                      tabIndex={0}
                      aria-label={event?.LocationSecondaryButtonCtaText}
                      iconName="CHEVRON_BLUE_RIGHT"
                      data-role="link"
                    />
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    } else return <></>;
  };

  const getPresenterDetails = () => {
    if (
      event?.Presenter &&
      (event?.Presenter?.PresenterName?.[localeCode] ||
        event?.Presenter?.PresenterImage?.assetToPublicLink?.results?.length >
          0 ||
        event?.Presenter?.PresenterBio?.[localeCode])
    ) {
      const presenterName = event?.Presenter?.PresenterName?.[localeCode];
      const presenterImageUrl = getImageURLForCHPages(
        event?.Presenter?.PresenterImage?.assetToPublicLink?.results,
      );
      const presenterBio = event?.Presenter?.PresenterBio?.[localeCode];

      return (
        <div className="max-w-(--event-detail-container-width) mx-auto max-lg:px-4 empty:hidden">
          <div className="max-w-(--event-detail-container-width) mx-auto bg-lighter-blue p-6 sm:p-8 rounded-xl border border-soft-blue shadow-sm mt-6">
            <h3 className="text-[18px] font-medium leading-5 mb-4 text-charcoal-black mt-0 ">
              {aboutThePresenterText}
            </h3>
            <div className="flex flex-col sm:flex-row sm:items-center gap-6">
              {presenterImageUrl && (
                <div className="min-w-25 overflow-hidden shadow-md self-start sm:self-center w-45 h-53.25 rounded-[40px]">
                  <Image
                    src={presenterImageUrl}
                    alt={presenterName || "Presenter Image"}
                    width={180}
                    height={213}
                    className="object-cover w-full h-full"
                  />
                </div>
              )}
              <div className="flex-1">
                {presenterName && (
                  <h4 className="text-[28px] font-normal leading-9 text-teal-blue max-lg:mt-2">
                    {presenterName}
                  </h4>
                )}

                {presenterBio && (
                  <RichTextWithTable
                    className={
                      "text-charcoal-black text-sm mt-3 richtext-content"
                    }
                    field={{ value: presenterBio }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <></>;
  };

  const getFeaturedDetails = () => {
    const eventFeaturedImageUrl = getImageURLForCHPages(
      event?.EventFeaturedImageContent?.results?.[0]?.assetToPublicLink
        ?.results,
    );

    if (
      event?.EventFeaturedContentHeading ||
      event?.EventPresenterBio ||
      eventFeaturedImageUrl ||
      event?.EventFeaturedVideoContent ||
      event?.EventFeaturedContent ||
      event?.EventFeaturedCntPrmryLnk ||
      event?.EventFeaturedCntSecLnk
    ) {
      return (
        <div className="bg-ecolab-blue mx-auto max-lg:px-4 empty:hidden">
          <div className="max-w-(--event-detail-container-width) mx-auto py-6">
            <div className="rounded-xl overflow-hidden">
              {(event?.EventFeaturedVideoContent || eventFeaturedImageUrl) && (
                <div className="shrink-0 w-full h-auto bg-cover bg-center ">
                  {event?.EventFeaturedVideoContent ? (
                    <div className="w-full overflow-hidden">
                      <VideoIframe src={event.EventFeaturedVideoContent} />
                    </div>
                  ) : (
                    <div className="w-full overflow-hidden">
                      <Image
                        src={eventFeaturedImageUrl}
                        alt={"Event featured image"}
                        width={800}
                        height={450}
                        className="object-cover w-full h-full"
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="px-6 bg-white pb-5">
                {event?.EventFeaturedContentHeading && (
                  <h3 className="text-ecolab-blue font-normal leading-9 text-[24px] m-0 pt-6">
                    {event?.EventFeaturedContentHeading}
                  </h3>
                )}
                {event?.EventPresenterBio && (
                  <h3 className="text-ash-gray font-normal leading-6 text-[22px] pt-3 m-0">
                    {event?.EventPresenterBio}
                  </h3>
                )}
                {event?.EventFeaturedContent && (
                  <div className="font-normal text-charcoal-black space-y-1 wrap-anywhere pt-5 richtext-content">
                    <RichTextWithTable
                      field={{ value: event?.EventFeaturedContent }}
                    />
                  </div>
                )}
                <div className="flex flex-wrap flex-col max-lg:items-start lg:flex-row max-lg:[&>a:last-child]:mb-0 empty:hidden md:gap-y-1 pt-5">
                  {event?.EventFeaturedCntPrmryLnk && (
                    <Button
                      buttonType="primary-with-icon"
                      field={{
                        value: {
                          href: event.EventFeaturedCntPrmryLnk,
                          text:
                            event?.EventFeaturedCntPrmryLnkText ||
                            event.EventFeaturedCntPrmryLnk,
                          target: isExternalURL(event.EventFeaturedCntPrmryLnk)
                            ? "_blank"
                            : "",
                        },
                      }}
                      tabIndex={0}
                      aria-label={event?.EventFeaturedCntPrmryLnkText}
                      iconName="CHEVRON_BLUE_RIGHT"
                      data-role="link"
                    />
                  )}
                  {event?.EventFeaturedCntSecLnk && (
                    <Button
                      buttonType="secondary-with-icon"
                      field={{
                        value: {
                          href: event?.EventFeaturedCntSecLnk,
                          text:
                            event?.EventFeaturedCntSecLnkText ||
                            event?.EventFeaturedCntSecLnk,
                          target: isExternalURL(event.EventFeaturedCntSecLnk)
                            ? "_blank"
                            : "",
                        },
                      }}
                      tabIndex={0}
                      aria-label={event?.EventFeaturedCntSecLnkText}
                      iconName="CHEVRON_BLUE_RIGHT"
                      data-role="link"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return <></>;
  };
  return (
    <div className="event-details ch-details bg-white">
      <div className="max-w-(--event-detail-container-width) mx-auto bg-white max-lg:px-4 last:mb-6">
        <div className="flex flex-col gap-y-11 pb-5 mb-6">
          <div className="flex-1 space-y-4 pt-5 ">
            <div className="flex w-full">
              {event?.EventTitle && (
                <h1 className="text-[24px] leading-9 text-ecolab-blue lg:text-3xl font-normal lg:leading-[1.25em] mt-0 lg:mb-0 wrap-anywhere max-lg:self-start max-lg:mb-0 after:content-[''] after:block after:clear-both after:pt-3 after:w-full lg:after:w-100 after:border-b-2 after:border-teal">
                  {event?.EventTitle}
                </h1>
              )}
            </div>
            {event?.EventSubtitle && (
              <h2 className="text-[18px] leading-6  text-charcoal-black font-normal lg:text-[24px] lg:leading-8 lg:mt-0 mb-4">
                {event?.EventSubtitle}
              </h2>
            )}
            <div className="mb-7">
              <div className="text-[18px] leading-6 font-normal lg:text-[24px] lg:leading-8 text-charcoal-black mb-0">
                {dateRange && (
                  <span className="text-[24px] leading-9">{dateRange}</span>
                )}
              </div>
              <div className="text-charcoal-black text-[14px] font-medium leading-5 mt-1">
                {event?.CurrentTimeZone?.TimeZone?.[localeCode]}
              </div>
            </div>
            {event?.EventLocation && (
              <div className="space-y-1 flex gap-1 items-start lg:items-center mb-8">
                <Icon icon={ICONS.LOCATION} className="mb-0" />
                <span className="text-blue-600 font-medium tracking-[0.8px] text-[16px] leading-6 text-ecolab-blue wrap-anywhere">
                  {event?.EventLocation}
                </span>
              </div>
            )}
            {(event?.EventMasterVideoURL || eventImageUrl) && (
              <div className="shrink-0 w-full h-auto rounded-lg overflow-hidden bg-cover bg-center mb-2 lg:mb-4">
                {event?.EventMasterVideoURL ? (
                  <div className="w-full">
                    <VideoIframe src={event.EventMasterVideoURL} />
                  </div>
                ) : (
                  <div className="w-full">
                    <Image
                      src={eventImageUrl}
                      alt={"Event detail image"}
                      width={800}
                      height={414}
                      className="object-cover h-full w-auto max-w-full rounded-md"
                      loading="eager"
                      fetchPriority="high"
                    />
                  </div>
                )}
              </div>
            )}
            <div className="text-charcoal-black text-[16px] tracking-[0.8px] leading-6 space-y-1">
              {event?.EventDescription && (
                <RichTextWithTable
                  className="wrap-anywhere richtext-content font-normal text-charcoal-black space-y-1"
                  field={{ value: event?.EventDescription }}
                />
              )}
            </div>
            {event?.LegacyHTML && (
              <>
                {event.LegacyHTML?.includes("ecolab-youtube-video") ? (
                  <RichTextWithConsent
                    htmlContent={event.LegacyHTML}
                    className="ch-legacy-content"
                  />
                ) : (
                  <RichTextWithTable
                    field={{ value: event.LegacyHTML }}
                    className="text-(--text-base) font-normal text-charcoal-black leading-6 tracking-[0.4px] lg:tracking-[0.8px] space-y-1 wrap-anywhere mb-5 richtext-content ch-legacy-content"
                  />
                )}
              </>
            )}
            <div className="flex w-full gap-x-2 flex-col lg:flex-row flex-wrap max-lg:[&>a:last-child]:mb-0 [&>a]:mr-0 md:gap-y-3.75">
              {event?.EventPrimaryButtonCTAUrl && (
                <Button
                  field={{
                    value: {
                      href: event.EventPrimaryButtonCTAUrl,
                      text:
                        event?.EventPrimayButtonCTAText ||
                        event?.EventPrimaryButtonCTAUrl,
                      target: isExternalURL(event.EventPrimaryButtonCTAUrl)
                        ? "_blank"
                        : "",
                    },
                  }}
                  buttonType="primary"
                  iconName=""
                  className=""
                  data-role="link"
                />
              )}
              {event?.EventsSecondaryButtonCTAUrl && (
                <Button
                  field={{
                    value: {
                      href: event.EventsSecondaryButtonCTAUrl,
                      text:
                        event?.EventSecondaryButtonCTAText ||
                        event?.EventsSecondaryButtonCTAUrl,
                      target: isExternalURL(event.EventsSecondaryButtonCTAUrl)
                        ? "_blank"
                        : "",
                    },
                  }}
                  buttonType="secondary"
                  iconName=""
                  className="btn-secondary mb-3.75"
                  data-role="link"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-light-gray pt-6 pb-6 empty:hidden">
        {getEventDetails()}
        {getLocationDetails()}
        {getPresenterDetails()}
      </div>
      {getFeaturedDetails()}
    </div>
  );
};
